import { useCallback, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { getCmdbUserPickerUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/user.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import type { User } from '@atlassian/jira-user-picker-next/src/services/fetch-users/types.tsx';
import type { InsightUser } from './types.tsx';

type UseUsersHookParams = {
	groups: string[];
	selectedUsers: User[];
};

export const fetchInsightAttributeUsers = async ({
	workspaceId,
	groups,
	query = '',
	excludes,
}: {
	workspaceId: string;
	groups: string[];
	query: string;
	excludes: User[];
}): Promise<User[]> => {
	const { users }: { users: InsightUser[] } = await performPostRequest(
		getCmdbUserPickerUrl(workspaceId),
		{
			body: JSON.stringify({
				excludes: excludes.map((user) => user.key).join(','),
				groups: groups.join(','),
				query,
			}),
		},
	);

	return users.map(({ key, displayName, avatarUrl, accountType }) => ({
		key,
		avatarUrl,
		id: key,
		type: 'user',
		name: displayName,
		fixed: false,
		accountType,
	}));
};

export const useUserAttributeUsers = ({ groups, selectedUsers }: UseUsersHookParams) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { workspaceId } = useWorkspaceContext();

	const [data, setDataInternal] = useState<User[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<unknown>();

	// Wrapper around setDataInternal that ensures that the current set of
	// 'selected' users is always accesible by the consuming component.
	const setData = useCallback(
		(newData: User[]) => {
			const mergedAndDedupedWithSelectedUsers = uniqBy([...newData, ...selectedUsers], 'id');
			setDataInternal(mergedAndDedupedWithSelectedUsers);
		},
		[selectedUsers],
	);

	const userSearch = useCallback(
		async ({
			query = '',
		}: {
			query?: string;
			accountIds?: string[];
		}): Promise<undefined | User[]> => {
			if (workspaceId) {
				setLoading(true);
				try {
					const users = await fetchInsightAttributeUsers({
						workspaceId,
						groups,
						query,
						excludes: selectedUsers,
					});
					fireTrackAnalytics(createAnalyticsEvent({}), 'insightSearchUserAttribute succeeded');
					setData(users);

					return users;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (err: any) {
					fireErrorAnalytics({
						error: err,
						meta: {
							id: 'insightSearchUserAttribute',
							teamName: 'falcons',
							packageName: 'jiraServicedeskInsightUserAttributePicker',
						},
					});
					setError(error);
				} finally {
					setLoading(false);
				}
			}
			return undefined;
		},
		[createAnalyticsEvent, error, groups, selectedUsers, setData, workspaceId],
	);
	return [userSearch, { data: data ?? [], error, loading }] as const;
};

export const createUseUsersHook =
	({ groups, selectedUsers }: UseUsersHookParams) =>
	() =>
		useUserAttributeUsers({ groups, selectedUsers });
